import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { dmYhm } from "@/lib/dateFmt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Warning, CheckCircle, Buildings, ShieldCheck, ArrowsHorizontal, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * v1.25.17 — Super User: Transfer Complete Party
 * v1.25.21 — Multi-select: transfer many parties in one go
 *
 * Wizard steps:
 *  1. Pick source company, then check ANY number of parties from it.
 *  2. Pick destination company.
 *  3. Preview: per-party counts + aggregate totals + UTR conflicts.
 *  4. Type "TRANSFER PARTY" (single) or "TRANSFER PARTIES" (bulk) exactly.
 *  5. Execute. On failure the backend rolls back every already-moved
 *     party so nothing partial lands.
 */
export default function TransferParty() {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [transfers, setTransfers] = useState([]);
    const [sourceId, setSourceId] = useState("");
    const [destId, setDestId] = useState("");
    const [parties, setParties] = useState([]);
    const [selectedPartyIds, setSelectedPartyIds] = useState([]);
    const [search, setSearch] = useState("");
    const [step, setStep] = useState(1);
    const [preview, setPreview] = useState(null);   // {plans:[], grand_total_rows, ...}
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/super/companies").then((r) => setCompanies(r.data.companies || [])).catch(() => {});
        api.get("/super/party-transfers").then((r) => setTransfers(r.data.transfers || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!sourceId) { setParties([]); setSelectedPartyIds([]); return; }
        api.get("/parties", { headers: { "X-Impersonate-Company": sourceId } })
            .then((r) => setParties(Array.isArray(r.data) ? r.data : []))
            .catch(() => setParties([]));
        setSelectedPartyIds([]);
    }, [sourceId]);

    const filteredParties = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return parties;
        return parties.filter((p) => (p.name || "").toLowerCase().includes(q));
    }, [parties, search]);

    const toggleParty = (pid) => setSelectedPartyIds((prev) => prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);
    const selectAllVisible = () => setSelectedPartyIds(Array.from(new Set([...selectedPartyIds, ...filteredParties.map((p) => p.id)])));
    const clearSelection = () => setSelectedPartyIds([]);

    const isBulk = selectedPartyIds.length > 1;
    const requiredPhrase = isBulk ? "TRANSFER PARTIES" : "TRANSFER PARTY";

    const goToPreview = async () => {
        if (!sourceId || !destId || selectedPartyIds.length === 0) { toast.error("Pick source, at least one party, and destination"); return; }
        if (sourceId === destId) { toast.error("Source and destination must be different"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/super/party-transfer/bulk-preview", {
                party_ids: selectedPartyIds, source_company_id: sourceId, dest_company_id: destId,
            });
            setPreview(data);
            setStep(3);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
        } finally { setBusy(false); }
    };

    const executeTransfer = async () => {
        if (confirmText !== requiredPhrase) { toast.error(`Type ${requiredPhrase} exactly`); return; }
        setBusy(true);
        try {
            if (isBulk) {
                const { data } = await api.post("/super/party-transfer/bulk-execute", {
                    party_ids: selectedPartyIds, source_company_id: sourceId, dest_company_id: destId,
                    confirm: "TRANSFER PARTIES",
                });
                toast.success(`Moved ${data.moved} parties → ${preview.dest_company_name}`);
                localStorage.setItem("micm_impersonate_company", destId);
                // v1.28.96 — In-app nav preserves impersonation. Was: hard reload
                // via window.location.assign — collateral 401 could wipe the
                // just-set impersonation via the api.js interceptor.
                window.dispatchEvent(new CustomEvent("micm-impersonation-changed", { detail: { company_id: destId } }));
                navigate(`/admin/parties`);
            } else {
                const { data } = await api.post("/super/party-transfer/execute", {
                    party_id: selectedPartyIds[0], source_company_id: sourceId, dest_company_id: destId,
                    confirm: "TRANSFER PARTY",
                });
                toast.success(`Moved ${data.audit.party_name} · ${data.audit.total_rows_moved} rows`);
                localStorage.setItem("micm_impersonate_company", destId);
                window.dispatchEvent(new CustomEvent("micm-impersonation-changed", { detail: { company_id: destId } }));
                navigate(`/admin/parties?highlight=${data.audit.party_id}`);
            }
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && (detail.conflicts || detail.plans)) {
                toast.error(detail.message || "Blocked · check UTR conflicts");
                setPreview((p) => p ? { ...p, blocked: true, grand_conflicts: detail.conflicts || p.grand_conflicts } : p);
            } else {
                toast.error(typeof detail === "string" ? detail : "Transfer failed");
            }
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="transfer-party-page">
            <div>
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-700">
                    <ShieldCheck size={14} weight="fill" /> Super User only
                </div>
                <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900">Transfer Complete Party</h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-500">
                    Move one or many parties — with every ledger entry, payment slip, UTR, pricing rule and customer login — from one company to another. This is a <b>move</b>, not a copy. On any failure the whole batch is rolled back so nothing partial lands.
                </p>
            </div>

            {step <= 2 && (
                <div className="grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:grid-cols-[1fr_auto_1fr]">
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">Source Company</Label>
                        <Select value={sourceId} onValueChange={setSourceId}>
                            <SelectTrigger data-testid="src-company" className="mt-1"><SelectValue placeholder="Pick the company that currently owns the parties" /></SelectTrigger>
                            <SelectContent>
                                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.parties_count} parties</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <div className="mt-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-[10px] uppercase tracking-widest text-slate-500">
                                    Parties · {selectedPartyIds.length} selected {isBulk && <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-800">Bulk</span>}
                                </Label>
                                {sourceId && (
                                    <div className="flex gap-2 text-[10px]">
                                        <button type="button" data-testid="select-all-parties" onClick={selectAllVisible} className="font-semibold text-indigo-700 hover:underline">Select {filteredParties.length}</button>
                                        <button type="button" data-testid="clear-parties" onClick={clearSelection} className="font-semibold text-slate-500 hover:underline">Clear</button>
                                    </div>
                                )}
                            </div>
                            <div className="mt-1 flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2">
                                <MagnifyingGlass size={12} className="text-slate-400" />
                                <input
                                    data-testid="party-search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    disabled={!sourceId}
                                    placeholder={sourceId ? "Search party by name…" : "Pick a source company first"}
                                    className="h-9 w-full border-0 bg-transparent text-sm outline-none"
                                />
                            </div>
                            <div className="mt-2 max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-slate-50/40" data-testid="party-multiselect">
                                {filteredParties.length === 0 && sourceId && <div className="p-3 text-sm text-slate-500">No parties match.</div>}
                                {filteredParties.map((p) => {
                                    const checked = selectedPartyIds.includes(p.id);
                                    return (
                                        <label key={p.id} data-testid={`party-check-${p.id}`} className={`flex items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0 hover:bg-white ${checked ? "bg-indigo-50/70" : ""}`}>
                                            <input type="checkbox" checked={checked} onChange={() => toggleParty(p.id)} className="h-4 w-4 accent-indigo-600" />
                                            <span className="flex-1 truncate">{p.name}</span>
                                            {checked && <ArrowRight size={12} className="text-indigo-700" />}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-center">
                        <ArrowsHorizontal size={26} weight="bold" className="text-slate-400" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">Destination Company</Label>
                        <Select value={destId} onValueChange={setDestId}>
                            <SelectTrigger data-testid="dest-company" className="mt-1"><SelectValue placeholder="Pick the destination company" /></SelectTrigger>
                            <SelectContent>
                                {companies.filter((c) => c.id !== sourceId).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <div className="mt-6 flex justify-end">
                            <Button
                                data-testid="preview-transfer-btn"
                                onClick={goToPreview}
                                disabled={busy || !sourceId || selectedPartyIds.length === 0 || !destId}
                                className="bg-amber-600 text-white hover:bg-amber-700"
                            >
                                Review {selectedPartyIds.length} {selectedPartyIds.length === 1 ? "party" : "parties"} <ArrowRight size={14} className="ml-1" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {step === 3 && preview && (
                <div className="space-y-4 rounded-xl border-2 border-amber-300 bg-amber-50/40 p-5 shadow-sm">
                    <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-amber-800">
                        <Warning size={16} weight="fill" /> Final review — nothing has changed yet
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div className="rounded-lg border border-slate-200 bg-white p-3">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500">Parties</div>
                            <div className="mt-1 font-display text-2xl font-bold text-slate-900" data-testid="preview-party-count">{preview.party_count}</div>
                            <div className="mt-1 text-[11px] text-slate-500">Total rows to move: <span className="font-mono font-bold text-slate-800">{preview.grand_total_rows}</span></div>
                        </div>
                        <div className="rounded-lg border border-rose-200 bg-white p-3">
                            <div className="text-[10px] uppercase tracking-widest text-rose-500">From (source)</div>
                            <div className="mt-1 flex items-center gap-1 font-display text-lg font-bold text-rose-800">
                                <Buildings size={14} weight="duotone" /> {companies.find((c) => c.id === sourceId)?.name}
                            </div>
                        </div>
                        <div className="rounded-lg border border-emerald-200 bg-white p-3">
                            <div className="text-[10px] uppercase tracking-widest text-emerald-600">To (destination)</div>
                            <div className="mt-1 flex items-center gap-1 font-display text-lg font-bold text-emerald-800">
                                <Buildings size={14} weight="duotone" /> {companies.find((c) => c.id === destId)?.name}
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-600">Per-party summary</div>
                        <div className="max-h-64 overflow-y-auto">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                                    <tr><th className="p-2">Party</th><th className="p-2 text-right">Balance</th><th className="p-2 text-right">Rows</th><th className="p-2">Status</th></tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {preview.plans.map((p) => (
                                        <tr key={p.party_id} data-testid={`plan-row-${p.party_id}`}>
                                            <td className="p-2 font-semibold text-slate-900">{p.party_name || p.party_id.slice(0, 8)}</td>
                                            <td className="p-2 text-right font-mono">₹{Number(p.balance || 0).toLocaleString("en-IN")}</td>
                                            <td className="p-2 text-right font-mono tabular-nums">{p.total_rows || 0}</td>
                                            <td className="p-2">{p.blocked ? <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-rose-800">Blocked</span> : <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-800">Ready</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {preview.blocked && (
                        <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-3" data-testid="bulk-blocked">
                            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-rose-800">Blocked — resolve every "Blocked" row above before transferring.</div>
                        </div>
                    )}

                    <div className="rounded-lg border border-slate-300 bg-white p-3">
                        <Label className="text-[10px] uppercase tracking-widest text-slate-500">
                            Type <b className="font-mono text-slate-900">{requiredPhrase}</b> to confirm this move
                        </Label>
                        <Input
                            data-testid="confirm-input"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={requiredPhrase}
                            className="mt-1 font-mono"
                            disabled={preview.blocked}
                        />
                    </div>

                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button variant="outline" onClick={() => { setStep(1); setPreview(null); setConfirmText(""); }}>Back</Button>
                        <Button
                            data-testid="execute-transfer-btn"
                            onClick={executeTransfer}
                            disabled={busy || preview.blocked || confirmText !== requiredPhrase}
                            className="bg-rose-700 text-white hover:bg-rose-800"
                        >
                            <CheckCircle size={14} className="mr-1.5" /> {busy ? "Transferring…" : `Transfer ${preview.party_count} ${preview.party_count === 1 ? "party" : "parties"} · ${preview.grand_total_rows} rows`}
                        </Button>
                    </div>
                </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="font-display text-lg font-bold text-slate-900">Recent Transfers</h2>
                    <div className="text-[10px] text-slate-500">Every move is audited</div>
                </div>
                {transfers.length === 0 && <div className="text-sm text-slate-500">No transfers yet.</div>}
                {transfers.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="p-2">When</th><th className="p-2">Party</th><th className="p-2">Source → Destination</th><th className="p-2 text-right">Rows</th><th className="p-2">By</th><th className="p-2">Batch</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {transfers.map((t) => (
                                    <tr key={t.id}>
                                        <td className="p-2 whitespace-nowrap font-mono text-[11px]">{dmYhm(t.completed_at)}</td>
                                        <td className="p-2 font-semibold text-slate-900">{t.party_name}</td>
                                        <td className="p-2"><span className="text-rose-700">{t.source_company_name}</span> <ArrowRight size={11} className="inline" /> <span className="text-emerald-700">{t.dest_company_name}</span></td>
                                        <td className="p-2 text-right font-mono tabular-nums">{t.total_rows_moved}</td>
                                        <td className="p-2 text-slate-600">{t.performed_by}</td>
                                        <td className="p-2">{t.batch ? <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-indigo-800">Bulk</span> : ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
